import { useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { useParams, Link } from 'react-router-dom'
import { format } from 'date-fns'
import { fetchConversationDetail, exportConversations } from '../api/analytics'
import { Bot, Info, ThumbsDown, ThumbsUp, User as UserIcon } from 'lucide-react'

type ThreadContentPart = {
  type?: string
  text?: string
  value?: string
  content?: string
}

function extractMessageText(message: any): string {
  if (!message) return ''

  if (typeof message.text === 'string' && message.text.trim().length > 0) {
    return message.text
  }

  if (Array.isArray(message.content)) {
    const parts = (message.content as ThreadContentPart[])
      .map((part) => part?.text ?? part?.value ?? part?.content ?? '')
      .filter((segment) => segment && segment.trim().length > 0)
    if (parts.length > 0) {
      return parts.join('\n\n')
    }
  }

  const body = message?.body
  if (typeof body === 'string' && body.trim().length > 0) {
    return body
  }

  return ''
}

export default function ConversationDetail() {
  const { t } = useTranslation()
  const { id } = useParams<{ id: string }>()
  const conversationId = parseInt(id || '0')

  // Fetch conversation detail
  const { data: conversation, isLoading, error } = useQuery({
    queryKey: ['conversation', conversationId],
    queryFn: () => fetchConversationDetail(conversationId),
    enabled: conversationId > 0,
  })

  const handleCopyQA = () => {
    if (!conversation) return

    let qaText = ''
    conversation.messages.forEach((msg) => {
      if (msg.type === 'user_message') {
        qaText += `**Q:** ${extractMessageText(msg)}\n\n`
      } else if (msg.type === 'assistant_message') {
        qaText += `**A:** ${extractMessageText(msg)}\n\n`
      }
    })

    navigator.clipboard.writeText(qaText)
    alert(t('conversationDetail.actions.copied'))
  }

  const handleExport = async () => {
    if (!conversation) return

    try {
      const result = await exportConversations({
        conversation_ids: [conversation.id],
        include_metadata: true,
      })

      // Download as file
      const blob = new Blob([result.content], { type: 'text/markdown;charset=utf-8' })
      const url = URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = `conversation-${conversation.id}.md`
      a.click()
      URL.revokeObjectURL(url)

      alert(t('conversations.export.success', { total: result.count }))
    } catch (err) {
      alert(`${t('conversations.export.error')}: ${err instanceof Error ? err.message : t('common.unknownError')}`)
    }
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-slate-400">{t('conversationDetail.loading')}</div>
      </div>
    )
  }

  if (error) {
    return (
      <div className="bg-rose-500/10 border border-rose-500/40 rounded-xl p-4">
        <h3 className="text-rose-200 font-medium">{t('conversationDetail.errorLoading')}</h3>
        <p className="text-rose-300 text-sm mt-1">
          {error instanceof Error ? error.message : t('common.unknownError')}
        </p>
      </div>
    )
  }

  if (!conversation) {
    return (
      <div className="bg-amber-500/10 border border-amber-500/40 rounded-xl p-4">
        <p className="text-amber-200">{t('conversationDetail.notFound')}</p>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Breadcrumb */}
      <nav className="text-sm text-muted-200">
        <Link to="/conversations" className="text-primary-200 hover:text-primary-100">
          {t('layout.conversations')}
        </Link>
        <span className="mx-2 text-muted-300">/</span>
        <span className="text-slate-100">{t('conversationDetail.metadata.conversationId')} {conversation.id}</span>
      </nav>

      {/* Header */}
      <div className="flex items-center justify-between">
        <h2 className="text-3xl font-semibold text-white">
          {t('conversationDetail.metadata.conversationId')} #{conversation.id}
        </h2>

        <div className="flex gap-3">
          <button
            onClick={handleCopyQA}
            className="px-4 py-2 rounded-lg bg-primary-500/20 border border-primary-500/50 text-primary-100 hover:bg-primary-500/30 transition-colors"
          >
            {t('conversationDetail.actions.copyQA')}
          </button>
          <button
            onClick={handleExport}
            className="px-4 py-2 rounded-lg bg-emerald-500/20 border border-emerald-500/40 text-emerald-200 hover:bg-emerald-500/30 transition-colors"
          >
            {t('conversationDetail.actions.exportMd')}
          </button>
        </div>
      </div>

      {/* Metadata Card */}
      <div className="rounded-xl border border-slate-800 bg-surface-200/80 backdrop-blur p-6">
        <h3 className="text-lg font-semibold text-slate-100 mb-4">{t('conversationDetail.metadata.title')}</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          <MetadataItem label={t('conversationDetail.metadata.conversationId')} value={conversation.thread_id} />
          <MetadataItem label={t('conversationDetail.metadata.site')} value={conversation.site_code.toUpperCase()} />
          <MetadataItem label={t('conversationDetail.metadata.user')} value={conversation.user_id ?? 'Anonymous'} />
          <MetadataItem
            label={t('conversationDetail.metadata.created')}
            value={format(new Date(conversation.created_at), 'MMM dd, yyyy HH:mm:ss')}
          />
          <MetadataItem label={t('conversationDetail.metadata.model')} value={conversation.model} />
          <MetadataItem
            label={`${t('conversationDetail.metadata.tokens')} (${t('conversationDetail.metadata.input')} / ${t('conversationDetail.metadata.output')})`}
            value={`${conversation.token_usage_in || 0} / ${conversation.token_usage_out || 0}`}
          />
      {typeof conversation.token_cost_usd === 'number' && (
        <MetadataItem
          label="Cost (USD)"
          value={`$${(conversation.token_cost_usd || 0).toFixed(4)}`}
        />
      )}
          {conversation.country_name && (
            <MetadataItem
              label={t('conversationDetail.metadata.country')}
              value={`${conversation.country_name} (${conversation.country_code})`}
            />
          )}
          {conversation.ip && (
            <MetadataItem label={t('conversationDetail.metadata.ipAddress')} value={conversation.ip} />
          )}
          {conversation.session_id && (
            <MetadataItem label="Session ID" value={conversation.session_id} />
          )}
          {(conversation.total_likes > 0 || conversation.total_dislikes > 0) && (
            <MetadataItem
              label={t('conversationDetail.metadata.feedback')}
              value={`${conversation.total_likes} 👍 / ${conversation.total_dislikes} 👎`}
            />
          )}
          {conversation.error_code && (
            <MetadataItem
              label={t('conversationDetail.metadata.error')}
              value={conversation.error_code}
              variant="danger"
            />
          )}
          {conversation.error_message && (
            <MetadataItem
              label={t('conversationDetail.metadata.error')}
              value={conversation.error_message}
              variant="danger"
            />
          )}
        </div>
      </div>

      {/* Messages */}
      <div className="rounded-xl border border-slate-800 bg-surface-200/80 backdrop-blur p-6">
        <h3 className="text-lg font-semibold text-slate-100 mb-4">
          {t('conversationDetail.messages.title')} ({conversation.messages.length})
        </h3>
        <div className="space-y-4">
          {conversation.messages.length === 0 ? (
            <p className="text-slate-400">{t('conversationDetail.messages.noMessages')}</p>
          ) : (
            conversation.messages.map((msg, index) => (
              <MessageItem key={index} message={msg} index={index} />
            ))
          )}
        </div>
      </div>
    </div>
  )
}

// ========================================
// MetadataItem Component
// ========================================

interface MetadataItemProps {
  label: string
  value: string
  variant?: 'default' | 'danger'
}

function MetadataItem({ label, value, variant = 'default' }: MetadataItemProps) {
  const valueColor =
    variant === 'danger' ? 'text-rose-200' : 'text-slate-100'

  return (
    <div>
      <dt className="text-xs uppercase tracking-wide text-muted-300">{label}</dt>
      <dd className={`mt-1 text-sm ${valueColor} break-all`}>{value}</dd>
    </div>
  )
}

// ========================================
// MessageItem Component
// ========================================

interface MessageItemProps {
  message: any
  index: number
}

function MessageItem({ message, index }: MessageItemProps) {
  const { t } = useTranslation()
  const isUser = message.type === 'user_message'
  const isAssistant = message.type === 'assistant_message'
  const [expanded, setExpanded] = useState(false)
  const feedback = message.feedback as { likes?: number; dislikes?: number } | undefined
  const hasLikes = (feedback?.likes ?? 0) > 0
  const hasDislikes = (feedback?.dislikes ?? 0) > 0

  if (!isUser && !isAssistant) {
    return (
      <div className="p-4 rounded-lg bg-surface-300 border border-slate-800/70">
        <div className="flex items-center justify-between">
          <div>
            <p className="flex items-center gap-2 text-xs font-medium text-muted-200">
              <Info className="h-4 w-4 text-muted-200" />
              {message.type ? message.type.replace(/_/g, ' ') : 'System item'}
            </p>
            <p className="text-xs text-muted-300 opacity-80">Message #{index + 1}</p>
          </div>
          <button
            onClick={() => setExpanded((prev) => !prev)}
            className="text-xs text-primary-200 hover:text-primary-100"
          >
            {expanded ? 'Hide details' : 'Show details'}
          </button>
        </div>
        {expanded && (
          <pre className="mt-3 text-sm text-slate-200 whitespace-pre-wrap overflow-auto">
            {JSON.stringify(message, null, 2)}
          </pre>
        )}
      </div>
    )
  }

  const content = extractMessageText(message)

  return (
    <div
      className={`p-4 rounded-lg ${
        isUser
          ? 'bg-primary-500/10 border-l-4 border-primary-400/60'
          : 'bg-emerald-500/10 border-l-4 border-emerald-400/60'
      }`}
    >
      <div className="flex items-center justify-between mb-2">
        <span className="flex items-center gap-2 text-xs font-medium text-muted-200">
          {isUser ? (
            <UserIcon className="h-4 w-4 text-muted-200" />
          ) : (
            <Bot className="h-4 w-4 text-muted-200" />
          )}
          {isUser ? t('conversationDetail.messages.user') : t('conversationDetail.messages.assistant')}
        </span>
        <span className="text-xs text-muted-300 opacity-80">Message #{index + 1}</span>
      </div>
      <div className="text-sm text-slate-100 whitespace-pre-wrap">{content}</div>
      {(hasLikes || hasDislikes) && (
        <div className="flex items-center gap-3 text-xs text-muted-200 mt-3">
          {hasLikes && (
            <span className="inline-flex items-center gap-1 text-emerald-300">
              <ThumbsUp className="h-3 w-3" />
              {feedback?.likes}
            </span>
          )}
          {hasDislikes && (
            <span className="inline-flex items-center gap-1 text-rose-300">
              <ThumbsDown className="h-3 w-3" />
              {feedback?.dislikes}
            </span>
          )}
        </div>
      )}
    </div>
  )
}
